import { z } from 'zod'

import { BULK_OPERATION_MAX_IDS } from './bulk-operations'

// =====================
// Unified archive / restore.
//
// Scrambl stores "this record is gone" four different ways, for historical
// reasons that are not worth a 55-table migration to undo:
//
//   deletedAt   — the soft-delete timestamp used by ~55 models
//   archivedAt  — a retire-but-don't-delete timestamp (notes, retainer plans)
//   isArchived  — a boolean toggle (tasks, sprints, cycles, buckets, types)
//   recordState — the T2 lifecycle marker on the merge spine (contact/deal/entity),
//                 where 'archived' is one of four states
//   status      — Project uses the literal status 'archived'
//
// This module does NOT unify the storage. It unifies the CONCEPT: one registry
// declares, per module, where its "gone" bit lives, and one read surface answers
// "what is archived here and how do I get it back". That is deliberately a
// presentation-layer unification — a schema migration that collapsed four
// mechanisms into one would have to rewrite live rows across 55 tables and every
// list query that filters them, which is exactly the kind of risk this feature
// exists to protect people from.
//
// RESTORE IS THE DANGEROUS HALF. Bringing a row back can:
//   - dangle, if its parent was deleted in the meantime, or
//   - collide, if its unique key (org+code, org+slug, org+sku) was reused while
//     it was away — Postgres has no partial unique index excluding archived rows
//     anywhere in this schema, so every unique constraint is live against
//     archived rows' keys the moment they come back.
// Both are checked BEFORE the write and reported as loud per-record failures.
// Nothing is ever "restored" into a corrupt state.
// =====================

export type ArchiveMechanismKind =
  | 'deletedAt'
  | 'archivedAt'
  | 'isArchived'
  | 'recordState'
  | 'status'

// How a given module's archived records come back:
//   'generic'  — this service can restore it directly (flip the field, after the
//                parent + uniqueness preflight passes)
//   'delegate' — the owning module already has an unarchive/restore endpoint that
//                does cascade work this service must not duplicate (Project
//                restores stashed status + child notes; Entity restores the
//                stashed lifecycle stage and cascades to projects; Task restores
//                a descendant tree). The archive view shows the record and points
//                at that endpoint.
//   'withheld' — restore is deliberately not offered. Visibility only.
export type ArchiveRestoreStrategy = 'generic' | 'delegate' | 'withheld'

export interface ArchiveModuleDescriptor {
  moduleType: string
  // Plural label for the tab / heading ("Contacts", "Vendors").
  label: string
  // What "gone" means here, in the order the UI should present them.
  mechanisms: {
    kind: ArchiveMechanismKind
    // Label for this lane ("Deleted", "Archived").
    label: string
    restoreStrategy: ArchiveRestoreStrategy
    // Populated for 'delegate' — the endpoint the client should call instead.
    delegateEndpoint?: string
    // Populated for 'withheld' — why, in words a user can act on.
    withheldReason?: string
  }[]
}

export interface ArchivedRecordSummary {
  moduleType: string
  label: string
  // Counts per mechanism lane, keyed by ArchiveMechanismKind.
  counts: Record<string, number>
  total: number
}

export interface ArchivedRecord {
  id: string
  moduleType: string
  // Best available human name for the row.
  name: string
  // Secondary identifier shown beside the name (code, number, slug).
  reference?: string | null
  mechanism: ArchiveMechanismKind
  // When it went away. Null for pure-boolean mechanisms with no timestamp column.
  archivedAt: string | null
  restoreStrategy: ArchiveRestoreStrategy
  delegateEndpoint?: string
  // Preflight verdict, computed server-side so the UI can grey out a row and say
  // why BEFORE anyone clicks Restore.
  restorable: boolean
  blockedReason?: string
}

export interface ArchivedRecordsPage {
  moduleType: string
  records: ArchivedRecord[]
  total: number
  page: number
  pageSize: number
}

export const ARCHIVE_LIST_MAX_PAGE_SIZE = 100

export const ArchiveListQuerySchema = z.object({
  mechanism: z
    .enum(['deletedAt', 'archivedAt', 'isArchived', 'recordState', 'status'])
    .optional(),
  search: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(ARCHIVE_LIST_MAX_PAGE_SIZE)
    .default(25),
})

export const ArchiveRestoreRequestSchema = z.object({
  moduleType: z.string().min(1),
  ids: z.array(z.string().uuid()).min(1).max(BULK_OPERATION_MAX_IDS),
  // Which lane the ids came from. Required: the same id can be both
  // soft-deleted AND archived (Sprint, Task, Note, Document all carry two
  // mechanisms), and restoring the wrong one would look like a no-op.
  mechanism: z.enum([
    'deletedAt',
    'archivedAt',
    'isArchived',
    'recordState',
    'status',
  ]),
})
export type ArchiveRestoreRequest = z.infer<typeof ArchiveRestoreRequestSchema>
